import React from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSync } from "@fortawesome/free-solid-svg-icons";

const DesktopTripSummary = ({ tripData, itinerary, estimateData, onRefresh, isRefreshing, loading, refreshCooldown }) => {
  if (!tripData) return null;

  const destinations = tripData.destinations || [];
  
  // Use itinerary.tripSummary.citiesBreakdown if available to correctly handle duplicate cities with different nights
  const cityPlans = itinerary?.tripSummary?.citiesBreakdown || [];
  
  const nightsArray = destinations.map((d, index) => {
    // If we have generated cityPlans, use the nights from the matching index
    if (cityPlans[index] && (cityPlans[index].city === d.name || cityPlans[index].city === d.city)) {
      return cityPlans[index].nights;
    }
    
    // Fallback to customNightsPerDestination map
    let n = 0;
    if (tripData.customNightsPerDestination && tripData.customNightsPerDestination[d.name] !== undefined) {
      n = tripData.customNightsPerDestination[d.name];
    } else if (tripData.customNightsPerDestination && tripData.customNightsPerDestination[d.city] !== undefined) {
      n = tripData.customNightsPerDestination[d.city];
    }
    return n;
  });

  const daysArray = [...nightsArray];
  if (daysArray.length > 0) {
    daysArray[0] += 1;
  }

  const destNames = destinations.map((d, index) => {
    const n = nightsArray[index];
    const days = daysArray[index] === 0 ? 1 : daysArray[index];
    return `${d.name} (${n}N/${days}D)`;
  }).join(", ") || "Unknown";


  const formatDateRange = (start, end) => {
    if (!start || !end) return "Dates TBD";
    const s = new Date(start);
    const e = new Date(end);
    const startDay = s.getDate();
    const endDay = e.getDate();
    const month = e.toLocaleString('default', { month: 'short', year: 'numeric' });
    return `${month.split(' ')[0]} ${startDay} - ${month.split(' ')[0]} ${endDay}, ${month.split(' ')[1]}`;
  };

  const pax = estimateData?.paxBreakdown || {};
  const a = pax.A || 0;
  const aeb = pax.AEB || 0;
  const cwb = pax.CwB || 0;
  const cnb = pax.CnB || 0;
  const totalAdults = a + aeb;
  const totalChildren = cwb + cnb;
  
  const paxStringParts = [];
  if (totalAdults > 0) paxStringParts.push(`${totalAdults} Adult${totalAdults > 1 ? 's' : ''}`);
  if (totalChildren > 0) paxStringParts.push(`${totalChildren} Child${totalChildren > 1 ? 'ren' : ''}`);
  const paxString = paxStringParts.join(", ") || "Unknown";

  const numRooms = tripData?.rooms?.length || 1;
  const startDate = tripData?.startDate ? new Date(tripData.startDate) : null;
  const endDate = tripData?.endDate ? new Date(tripData.endDate) : null;
  let nights = 1;
  if (startDate && endDate) {
    const diffTime = Math.abs(endDate - startDate);
    nights = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }
  if (nights === 0) nights = 1;

  return (
    <div className="hidden lg:flex items-center justify-between bg-[#f4f6fa] border border-[#e8ebf3] rounded-xl p-3 px-6 mb-8 mt-2">
      <div className="flex items-center gap-4 lg:gap-8 flex-1 min-w-0 mr-4">
        {/* Destination */}
        <div className="flex flex-col flex-1 min-w-[150px]">
          <span className="text-[10px] text-gray-500 font-medium mb-0.5">Destination</span>
          <span className="text-sm font-semibold text-gray-900 line-clamp-2 break-words" title={destNames}>{destNames}</span>
        </div>

        <div className="w-px h-8 bg-gray-200 shrink-0"></div>

        {/* Travel Dates */}
        <div className="flex flex-col shrink-0">
          <span className="text-[10px] text-gray-500 font-medium mb-0.5">Travel Dates</span>
          <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">{formatDateRange(tripData.startDate, tripData.endDate)}</span>
        </div>

        <div className="w-px h-8 bg-gray-200 shrink-0"></div>

        {/* Travelers */}
        <div className="flex flex-col shrink-0">
          <span className="text-[10px] text-gray-500 font-medium mb-0.5">Travelers</span>
          <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">{paxString}</span>
        </div>

        <div className="w-px h-8 bg-gray-200 shrink-0"></div>

        {/* Rooms */}
        <div className="flex flex-col shrink-0">
          <span className="text-[10px] text-gray-500 font-medium mb-0.5">Rooms</span>
          <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">{numRooms} Room{numRooms > 1 ? 's' : ''}</span>
        </div>

        <div className="w-px h-8 bg-gray-200 shrink-0"></div>

        {/* Nights */}
        <div className="flex flex-col shrink-0">
          <span className="text-[10px] text-gray-500 font-medium mb-0.5">Duration</span>
          <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">{nights} Nights / {nights + 1} Days</span>
        </div>
      </div>

      {/* Refresh Pricing Button */}
      {estimateData?.totalPax > 0 && (
        <button 
          onClick={onRefresh}
          disabled={isRefreshing || loading || refreshCooldown}
          className="flex items-center gap-2 px-4 py-2 bg-transparent text-teal-700 hover:text-teal-800 rounded-lg text-xs font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FontAwesomeIcon icon={faSync} className={isRefreshing ? "animate-spin" : ""} />
          {isRefreshing ? 'Refreshing...' : refreshCooldown ? 'Please Wait...' : 'Refresh Pricing'}
        </button>
      )}
    </div>
  );
};

export default DesktopTripSummary;
